import React from 'react';
import type { SituationSortCategory } from '../../types';
import { getVisibleTextFromInlineTerms } from '../../utils/inline-terms';
import { FormattedInlineText } from '../formatted-inline-text';
import { InlineEditableText } from './InlineEditableText';
import type { SortCategoryActions } from './sort-category-actions';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';

interface SortCategoriesModalProps {
  open: boolean;
  onClose: () => void;
  sortCategoryActions: SortCategoryActions;
}

function categoryVisibleLabel(category: SituationSortCategory): string {
  return getVisibleTextFromInlineTerms(category.label) || category.id;
}

export const SortCategoriesModal: React.FC<SortCategoriesModalProps> = ({
  open,
  onClose,
  sortCategoryActions,
}) => {
  if (!open) return null;

  const categories = sortCategoryActions.globalCategories;

  return (
    <div className={studio.modalOverlay} onClick={onClose}>
      <div
        className={studio.modal}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sort-categories-modal-title"
      >
        <header className={studio.modalHeader}>
          <h3 id="sort-categories-modal-title">Catégories de tri du scénario</h3>
          <button type="button" className={studio.btnSecondary} onClick={onClose}>
            Fermer
          </button>
        </header>

        <div className={studio.modalBody}>
          <p className={inline.globalSortCategoriesHint}>
            Ces catégories sont partagées entre tous les jeux de tri. Modifiez un libellé ici
            pour le mettre à jour partout.
          </p>

          <div className={inline.globalSortCategoriesHeader}>
            <span className={inline.globalSortCategoriesTitle}>
              {categories.length} catégorie{categories.length > 1 ? 's' : ''}
            </span>
            <button
              type="button"
              className={studio.btnSecondary}
              onClick={() => sortCategoryActions.addGlobalCategory()}
            >
              + Catégorie
            </button>
          </div>

          {categories.length === 0 ? (
            <p className={inline.globalSortCategoriesHint}>
              Aucune catégorie pour l&apos;instant. Ajoutez-en une pour la réutiliser dans vos
              jeux de tri.
            </p>
          ) : (
            <div className={inline.globalSortCategoriesList}>
              {categories.map((category, index) => (
                <div key={category.id} className={inline.globalSortCategoryItem}>
                  <div className={inline.globalSortCategoryMeta}>
                    <span className={inline.globalSortCategoryIndex}>#{index + 1}</span>
                    <button
                      type="button"
                      className={inline.globalSortCategoryRemove}
                      onClick={() => sortCategoryActions.removeGlobalCategory(category.id)}
                      title="Supprimer de la liste globale"
                    >
                      ×
                    </button>
                  </div>
                  <InlineEditableText
                    value={category.label}
                    editMode
                    className={inline.globalSortCategoryInput}
                    onChange={(label) =>
                      sortCategoryActions.updateGlobalLabel(category.id, label)
                    }
                    placeholder="Libellé (supporte **terme**(définition))…"
                  />
                  {category.label ? (
                    <span className={inline.globalSortCategoryPreview}>
                      <FormattedInlineText text={category.label} />
                    </span>
                  ) : (
                    <span className={inline.globalSortCategoryPreview}>
                      {categoryVisibleLabel(category)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
